import React from "react";
import styled from "styled-components";
import portfolio from "../content/portfolio.json";
import PageHelmet from "../components/PageHelmet";
import CertificationsPanel from "../components/CertificationsPanel";

const { meta, bio, certifications, education, skills, experience } = portfolio;

const skillCategoryLabels = {
    languages: "Languages & Frameworks",
    systems: "Systems & Infrastructure",
    tools: "Tools & Platforms",
};

const AboutContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 40px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 20px;
    }
`;

const LeftColumn = styled.div`
    flex: 1;
    text-align: left;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

const RightColumn = styled.div`
    flex: 2;
    text-align: left;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 10px;
    color: #0078d7;
`;

const Text = styled.p`
    font-size: 1rem;
    line-height: 1.8;
    color: #333;
    text-align: justify;
`;

const SkillsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

const SkillCategory = styled.div`
    text-align: left;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

const SkillCategoryTitle = styled.h3`
    font-size: 0.95rem;
    font-weight: 600;
    color: #555;
    margin: 0 0 8px 0;
`;

const SkillList = styled.ul`
    list-style-type: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const SkillItem = styled.li`
    font-size: 0.9rem;
    color: #555;
    background: #f5f9fd;
    border: 1px solid #e0e8f0;
    border-radius: 4px;
    padding: 4px 10px;
    white-space: nowrap;
`;

const ExperienceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const ExperienceItem = styled.div`
    border-left: 3px solid #0078d7;
    padding-left: 10px;
`;

const ExperienceTitle = styled.h3`
    font-size: 1.2rem;
    margin-bottom: 5px;
    color: #0078d7;
`;

const ExperienceSubtitle = styled.p`
    font-size: 0.9rem;
    color: #555;
    margin-bottom: 5px;
`;

const About = () => {
    return (
        <>
            <PageHelmet title={meta.pageTitles.about} />
            <AboutContainer>
                <LeftColumn>
                    <SectionTitle>{meta.name}</SectionTitle>
                    {bio.map((paragraph, i) => (
                        <Text key={i}>{paragraph}</Text>
                    ))}

                    <SectionTitle>
                        {certifications.sectionTitle || "Licenses & Certifications"}
                    </SectionTitle>
                    <CertificationsPanel />

                    <SectionTitle>Education</SectionTitle>
                    {education.map((entry) => (
                        <Text key={entry.degree}>
                            <strong>{entry.degree}</strong> <br />
                            {entry.institution}, {entry.years}
                        </Text>
                    ))}
                </LeftColumn>

                <RightColumn>
                    <SectionTitle>Professional Skills</SectionTitle>
                    <SkillsGrid>
                        {Object.entries(skills).map(([category, items]) => (
                            <SkillCategory key={category}>
                                <SkillCategoryTitle>
                                    {skillCategoryLabels[category] || category}
                                </SkillCategoryTitle>
                                <SkillList>
                                    {items.map((skill) => (
                                        <SkillItem key={skill}>{skill}</SkillItem>
                                    ))}
                                </SkillList>
                            </SkillCategory>
                        ))}
                    </SkillsGrid>

                    <SectionTitle>Work Experience</SectionTitle>
                    <ExperienceList>
                        {experience.map((job) => (
                            <ExperienceItem key={job.title + job.company}>
                                <ExperienceTitle>{job.title}</ExperienceTitle>
                                <ExperienceSubtitle>
                                    {job.company} | {job.period}
                                </ExperienceSubtitle>
                                <Text>{job.description}</Text>
                            </ExperienceItem>
                        ))}
                    </ExperienceList>
                </RightColumn>
            </AboutContainer>
        </>
    );
};

export default About;
